import { useState } from 'react';
import { useTranslation } from 'react-i18next';
import { Home, Pencil, Trash2, Users } from 'lucide-react';
import {
    SettingsCategory,
    SettingsVariable,
    getAffectedPolicies,
    removeCategory,
    renameCategory,
} from '../../services/api/departmentVariablesService';
import {
    DepartmentUser,
    categoryTitle,
    variableLabel,
} from './utils/settingsView';
import VariableRow from './variable-row/VariableRow';
import AddVariableRow from './AddVariableRow';
import { useToast } from '../../hooks/use-toast';
import { Card } from '../ui/card';
import { Button, buttonVariants } from '../ui/button';
import { Input } from '../ui/input';
import {
    AlertDialog,
    AlertDialogAction,
    AlertDialogCancel,
    AlertDialogContent,
    AlertDialogDescription,
    AlertDialogFooter,
    AlertDialogHeader,
    AlertDialogTitle,
} from '../ui/alert-dialog';

interface CategoryCardProps {
    category: SettingsCategory;
    variables: SettingsVariable[];
    allVariables: SettingsVariable[];
    tabCategories: SettingsCategory[];
    users: DepartmentUser[];
    onSaved: () => Promise<void>;
}

export default function CategoryCard({
    category,
    variables,
    allVariables,
    tabCategories,
    users,
    onSaved,
}: CategoryCardProps) {
    const { t } = useTranslation();
    const { toast } = useToast();
    const [isRenaming, setIsRenaming] = useState(false);
    const [draftTitle, setDraftTitle] = useState('');
    const [deleteDialogOpen, setDeleteDialogOpen] = useState(false);
    const [policyCounts, setPolicyCounts] = useState<Record<
        string,
        number
    > | null>(null);

    const title = categoryTitle(category, t);
    const description =
        category.kind === 'stored'
            ? ''
            : t(`departmentVariables.sections.${category.key}.description`, '');
    const isEditable = category.kind === 'legacy' || category.kind === 'stored';

    const saveTitle = async () => {
        const next = draftTitle.trim();
        if (!next || next === title) {
            setIsRenaming(false);
            return;
        }
        const taken = tabCategories
            .filter((other) => other.key !== category.key)
            .some(
                (other) =>
                    categoryTitle(other, t).toLowerCase() === next.toLowerCase()
            );
        if (taken) {
            toast({
                title: t('departmentVariables.category.titleExists'),
                variant: 'destructive',
            });
            return;
        }
        try {
            await renameCategory(category.key, next);
            await onSaved();
            setIsRenaming(false);
        } catch {
            return;
        }
    };

    const deleteBlocked = Object.values(policyCounts ?? {}).some(
        (count) => count > 0
    );

    const openDeleteDialog = async () => {
        setPolicyCounts(null);
        setDeleteDialogOpen(true);
        try {
            const counts = await Promise.all(
                variables
                    .filter((variable) => variable.stored)
                    .map(async (variable) => {
                        const policies = await getAffectedPolicies(
                            variable.name
                        );
                        return [variable.name, policies.length] as const;
                    })
            );
            setPolicyCounts(Object.fromEntries(counts));
        } catch {
            setPolicyCounts({});
        }
    };

    const deleteCategory = async () => {
        try {
            await removeCategory(category.key);
            await onSaved();
        } catch {
            return;
        }
    };

    return (
        <Card className="border-subtle overflow-hidden">
            <div className="p-4 flex items-center gap-4">
                <div className="flex h-10 w-10 shrink-0 items-center justify-center rounded-lg bg-muted text-subtle-foreground">
                    {category.tab === 'contacts' ? (
                        <Users className="h-5 w-5" />
                    ) : (
                        <Home className="h-5 w-5" />
                    )}
                </div>
                <div className="flex-1 min-w-0">
                    {isRenaming ? (
                        <Input
                            autoFocus
                            value={draftTitle}
                            className="h-9 text-sm"
                            placeholder={t(
                                'departmentVariables.category.categoryPlaceholder'
                            )}
                            onChange={(e) => setDraftTitle(e.target.value)}
                            onKeyDown={(e) => {
                                if (e.key === 'Enter') {
                                    e.preventDefault();
                                    void saveTitle();
                                }
                                if (e.key === 'Escape') setIsRenaming(false);
                            }}
                        />
                    ) : (
                        <span className="font-semibold">{title}</span>
                    )}
                    {description && (
                        <p className="text-sm text-subtle-foreground font-medium">
                            {description}
                        </p>
                    )}
                </div>
                {isEditable && !isRenaming && (
                    <div className="flex gap-2 shrink-0">
                        <Button
                            variant="outline"
                            size="icon"
                            aria-label={t(
                                'departmentVariables.category.rename'
                            )}
                            title={t('departmentVariables.category.rename')}
                            onClick={() => {
                                setDraftTitle(title);
                                setIsRenaming(true);
                            }}
                        >
                            <Pencil className="h-4 w-4" />
                        </Button>
                        <Button
                            variant="outline"
                            size="icon"
                            className="text-destructive hover:text-destructive"
                            aria-label={t(
                                'departmentVariables.category.delete'
                            )}
                            title={t('departmentVariables.category.delete')}
                            onClick={() => void openDeleteDialog()}
                        >
                            <Trash2 className="h-4 w-4" />
                        </Button>
                    </div>
                )}
                {isRenaming && (
                    <div className="flex gap-2 shrink-0">
                        <Button
                            variant="outline"
                            size="sm"
                            onClick={() => setIsRenaming(false)}
                        >
                            {t('departmentVariables.category.cancel')}
                        </Button>
                        <Button
                            size="sm"
                            onClick={() => void saveTitle()}
                            disabled={!draftTitle.trim()}
                        >
                            {t('departmentVariables.category.save')}
                        </Button>
                    </div>
                )}
            </div>

            <div className="border-t border-subtle">
                {variables.length === 0 && (
                    <p className="px-4 py-4 text-center text-sm text-subtle-foreground font-medium">
                        {t('departmentVariables.category.emptyCategory')}
                    </p>
                )}
                {variables.map((variable) => (
                    <VariableRow
                        key={variable.name}
                        variable={variable}
                        allVariables={allVariables}
                        tabCategories={tabCategories}
                        users={users}
                        onSaved={onSaved}
                    />
                ))}
                {category.kind !== 'other' && (
                    <AddVariableRow
                        category={category}
                        allVariables={allVariables}
                        users={users}
                        onSaved={onSaved}
                    />
                )}
            </div>

            <AlertDialog
                open={deleteDialogOpen}
                onOpenChange={setDeleteDialogOpen}
            >
                <AlertDialogContent>
                    <AlertDialogHeader>
                        <AlertDialogTitle>
                            {t('departmentVariables.category.deleteTitle', {
                                title,
                            })}
                        </AlertDialogTitle>
                        <AlertDialogDescription>
                            {deleteBlocked
                                ? t(
                                      'departmentVariables.category.deleteBlocked',
                                      { title }
                                  )
                                : variables.length === 0
                                  ? t(
                                        'departmentVariables.category.deleteEmpty'
                                    )
                                  : t(
                                        'departmentVariables.category.deleteDescription'
                                    )}
                        </AlertDialogDescription>
                    </AlertDialogHeader>
                    {variables.length > 0 && (
                        <ul className="list-disc pl-5 text-sm">
                            {variables.map((variable) => {
                                const count =
                                    policyCounts?.[variable.name] ?? 0;
                                return (
                                    <li key={variable.name}>
                                        {variableLabel(variable, t)}
                                        {count > 0 && (
                                            <span className="text-subtle-foreground font-medium">
                                                {' · '}
                                                {count === 1
                                                    ? t(
                                                          'departmentVariables.category.usedByOne'
                                                      )
                                                    : t(
                                                          'departmentVariables.category.usedByOther',
                                                          { count }
                                                      )}
                                            </span>
                                        )}
                                    </li>
                                );
                            })}
                        </ul>
                    )}
                    <AlertDialogFooter>
                        <AlertDialogCancel>
                            {t('departmentVariables.category.cancel')}
                        </AlertDialogCancel>
                        {policyCounts !== null && !deleteBlocked && (
                            <AlertDialogAction
                                className={buttonVariants({
                                    variant: 'destructive',
                                })}
                                onClick={() => void deleteCategory()}
                            >
                                {t('departmentVariables.category.delete')}
                            </AlertDialogAction>
                        )}
                    </AlertDialogFooter>
                </AlertDialogContent>
            </AlertDialog>
        </Card>
    );
}
